import { useState } from "react";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "~/components/ui/dialog";
import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";

// // // //

/**
 * RevokeConfirmDialog
 * Destructive-action confirmation that requires the user to type the client's
 * name before the confirm button is enabled, matching the type-to-confirm
 * pattern used elsewhere in the app.
 */
export function RevokeConfirmDialog(props: {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    clientName: string;
    loading?: boolean;
    title?: string;
    description?: React.ReactNode;
    confirmLabel?: string;
    onConfirm: () => void;
}) {
    const {
        open,
        onOpenChange,
        clientName,
        loading,
        title = "Revoke OAuth client",
        description,
        confirmLabel = "Revoke client",
        onConfirm,
    } = props;

    const [typed, setTyped] = useState("");
    const matches = typed.trim() === clientName.trim();

    return (
        <Dialog
            open={open}
            onOpenChange={(next) => {
                if (!next) setTyped("");
                onOpenChange(next);
            }}
        >
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>{title}</DialogTitle>
                    <DialogDescription>
                        {description ?? (
                            <>
                                This revokes the client and invalidates all of
                                its secrets and active sessions. This cannot be
                                undone.
                            </>
                        )}
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-2">
                    <Label htmlFor="revoke-confirm-name" className="text-sm">
                        Type{" "}
                        <span className="font-mono font-semibold">
                            {clientName}
                        </span>{" "}
                        to confirm.
                    </Label>
                    <Input
                        id="revoke-confirm-name"
                        value={typed}
                        autoComplete="off"
                        onChange={(event) => setTyped(event.target.value)}
                    />
                </div>

                <DialogFooter>
                    <Button
                        type="button"
                        variant="outline"
                        onClick={() => onOpenChange(false)}
                    >
                        Cancel
                    </Button>
                    <Button
                        type="button"
                        variant="destructive"
                        disabled={!matches || loading}
                        onClick={onConfirm}
                    >
                        {confirmLabel}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
